import { useState, useEffect, useRef } from "react";
import {  Row, 
          Col, 
          Form, 
          Button, 
          Input, 
          InputGroup, 
          Card, 
          CardBody, 
          Spinner, 
          Modal, 
          ModalHeader, 
          ModalBody, 
          ModalFooter } from "reactstrap";
import ComponentCard from "./ComponentCard";
import TreeNode from "./TreeNode";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons"
import '../../index.css'; 
import buildDynamicFileBatches from "./batchBuilder";
import pLimit from 'p-limit';
import { flushSync } from 'react-dom';
import { ErrorOutline } from "@mui/icons-material";
import DownloadDialog from './DownloadDialog';
import { Box, Typography, LinearProgress } from '@mui/material';
import { apiClient } from '../../service/ApiClient';
import NavbarComponent from '../Navbar'

const authService = require("../../service/AuthService");

const DataManagement = () => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [uploading, setIsUploading] = useState(false);  
  const [uploadProgress, setUploadProgress] = useState(0);
  const [folderTree, setFolderTree] = useState({});
  const [previewContent, setPreviewContent] = useState("");
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [collapsedFolders, setCollapsedFolders] = useState({}); 
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedFolders, setSelectedFolders] = useState([]);
  const folderInputRef = useRef(null);
  const [fileKey, setFileKey] = useState("");
  const [inputKey, setInputKey] = useState(0); 
  const [popupData, setPopupData] = useState(null);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [nextContinuationToken, setNextContinuationToken] = useState(null);
  const [fileTag, setFileTag] = useState("");
  const [existingS3FilesSet, setExistingS3FilesSet] = useState(new Set());
  const [labelPopupOpen, setLabelPopupOpen] = useState(false);
  const [rectangles, setRectangles] = useState({});
  const [currentRect, setCurrentRect] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isLabelingMode, setIsLabelingMode] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0); 
  const [labelingQueue, setLabelingQueue] = useState([]);
  const [labelModeName, setLabelModeName] = useState("multiple"); 
  const [newLabels, setNewLabels] = useState("");
  const [savedLabels, setSavedLabels] = useState("");
  const [showMultiLabelPopUp, setShowMultiLabelPopUp] = useState("false");
  const [multiLabelInput, setMultiLabelInput] = useState("");
  const [allRectangles, setAllRectangles] = useState({});

  const canvasRef = useRef(null);
  const previewContainerRef = useRef(null);
  const [mouseStart, setMouseStart] = useState(null);

  const user = authService.getUser();
  const organization = user.organization.toLowerCase();
  const username = user.name.toLowerCase();
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPath, setSelectedPath] = useState("");
  const [folderNameToUpload, setFolderNameToUpload] = useState("");
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [folderMetadata, setFolderMetadata] = useState({});
  const abortControllerRef = useRef(null);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [progressLabel, setProgressLabel] = useState("");
  const [deletionResult, setDeletionResult] = useState(null);
  const [datasetList, setDatasetList] = useState([]);
  const [selectedDataset, setSelectedDataset] = useState("");
  const [prevDataset, setPrevDataset] = useState(null);
  const allRectanglesRef = useRef({});

  const scrollContainerRef = useRef(null);
  const annotationDataRef = useRef([]);
  const fileNamesArrayRef = useRef([]);
  const imgRef = useRef(null);
  const [isImageReady, setIsImageReady] = useState(false);
  const [imgSize, setImgSize] = useState({ w: 1, h: 1 });
  const [naturalSize, setNaturalSize] = useState({ w: 1, h: 1 });
  const [fitBox, setFitBox] = useState({ left: 0, top: 0, w: 1, h: 1 }); // fitted image rect in container
  const [naturalSizeByFile, setNaturalSizeByFile] = useState({}); // { [filename]: { w, h } }
  const pendingTagsRef = useRef({}); // { [filePath]: rawTagsFromServer[] }


  const folderFileCache = {};

  useEffect(() => {
    const initializePage = async () => {
      await fetchDatasets();
    };
    initializePage();
  
    const handleScroll = () => {
      const container = scrollContainerRef.current;
      if (!container || isRefreshing) return;
  
      const scrollThreshold = 50;
      const isNearBottom =
        container.scrollTop + container.clientHeight >= container.scrollHeight - scrollThreshold;
  
      if (isNearBottom && nextContinuationToken) {
        console.log("Lazy loading triggered...");
        fetchFiles(selectedDataset, nextContinuationToken);
      }
    };
  
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleScroll);
    }
  
    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener("scroll", handleScroll);
      }
    };
  }, [
    isDownloading, 
    progressLabel, 
    nextContinuationToken, 
    isRefreshing, 
    selectedDataset,
    selectedFiles,
    selectedFolders,
    currentImageIndex,
    showMultiLabelPopUp,
    multiLabelInput,
    allRectangles
  ]);
  
  
  useEffect(() => {
    document.body.style.cursor = isLabelingMode ? "crosshair" : "default";
    return () => {
      document.body.style.cursor = "default";
    };
  }, [isLabelingMode]);


  useEffect(() => {
    if (previewContent?.startsWith("http")) {
      setLoadingPreview(true);
      setIsImageReady(false);
    } else {
      setLoadingPreview(false);
      setIsImageReady(false);
    }
  }, [previewContent]);


  useEffect(() => {
    if (!isLabelingMode) return;
    const file = labelingQueue[currentImageIndex];
    if (file) {
      // only this path loads the image in labeling mode
      handleSelectFile(file, true);
    }
  }, [isLabelingMode, currentImageIndex, labelingQueue]); 


  const computeFitBox = () => {
    const container = previewContainerRef.current;
    const img = imgRef.current;
    if (!container || !img) return;

    const Cw = container.clientWidth;
    const Ch = container.clientHeight;
    const { naturalWidth: Iw, naturalHeight: Ih } = img;

    const scale = Math.min(Cw / Iw, Ch / Ih);
    const w = Iw * scale;
    const h = Ih * scale;
    const left = (Cw - w) / 2;
    const top  = (Ch - h) / 2;

    setFitBox({ left, top, w, h });
  };

  useEffect(() => {
    // recompute on window resize too
    const ro = new ResizeObserver(computeFitBox);
    if (previewContainerRef.current) ro.observe(previewContainerRef.current);
    return () => ro.disconnect();
  }, []);


  // Convert pixel rect -> normalized [0..1] using natural size
  const pxToNorm = (t, nat) => ({
    xMin: t.xmin / nat.w,
    yMin: t.ymin / nat.h,
    xMax: t.xmax / nat.w,
    yMax: t.ymax / nat.h,
  });

  // Convert normalized -> pixel using natural size
  const normToPx = (r, nat) => ({
    xMin: r.xMin * nat.w,
    yMin: r.yMin * nat.h,
    xMax: r.xMax * nat.w,
    yMax: r.yMax * nat.h,
  });


  const fetchDatasets = async () => {
    try {
      const response = await apiClient.get('datamanagement', {
        params: { organization }
      });

      const rawMetadata = response.data.folderMetadata || [];
  
      // This array will be used for the dropdown
      const datasets = rawMetadata.map(item => ({
        folderName: item.folderName,
        updatedttm: item.updatedttm,
        file_count: item.file_count
      }));
  
      // This object will be used to quickly lookup folder data inside TreeNode
      const folderMetadata = {};
      rawMetadata.forEach(item => {
        folderMetadata[item.folderName] = {
          updatedttm: item.updatedttm,
          file_count: item.file_count
        };
      });
  
      setDatasetList(datasets);
      setFolderMetadata(folderMetadata);
  
      return { datasets, folderMetadata };
    } catch (e) {
      console.error("Failed to fetch dataset list", e);
      return { datasets: [], folderMetadata: {} };
    }
  };
  

  const handleDatasetChange = (e) => {
    const selectedDataset = e.target.value;
    setSelectedDataset(selectedDataset);
    fetchFiles(selectedDataset); 
  };

  // Fetch files from API
  const fetchFiles = async (dataset, datasetFolderMap = null) => {
    try {
      setIsRefreshing(true);
      setProgressLabel("Retrieving files...");
  
      if (!organization) {
        console.error("Error: Organization is undefined");
        return;
      }
  
      const response = await apiClient.get('datamanagement', {
        params: {
          organization,
          dataset,
          datasetFolderMap
        }
      });

      // If switching dataset, reset tree
      if (dataset !== prevDataset) {
        setFolderTree({});
        setPrevDataset(dataset);
      }
      
      const fileNamesArray = response.data.flatMap(item => {
        return item.files.map(file => `${file}`);
      });

      const existingFileSet = new Set(
        fileNamesArray.map(path => path.replace(/\.[^/.]+$/, "")) 
      );
      setExistingS3FilesSet(existingFileSet);

      fileNamesArrayRef.current = fileNamesArray.map((path) =>
        path.split("/").slice(2).join("/")
      );
  
      const tree = buildFolderTree(fileNamesArray);
  
      if (tree) {
        setFolderTree(prevTree => ({
          ...prevTree,
          ...tree
        }));
      }
  
    } catch (error) {
      console.error("Error in API call:", error);
    } finally {
      setIsRefreshing(false);
      setProgressLabel("");
    }
  };


  // Convert flat file list into a nested folder structure
  const buildFolderTree = (filePaths) => {
    const tree = {};
  
    filePaths.forEach((path) => {
      // Remove trailing slash if it's a folder path
      if (path.endsWith("/")) {
        path = path.slice(0, -1);
      }
  
      const parts = path.split("/");
  
      // Skip the first two parts: [orgName, "data"]
      const relativeParts = parts.slice(2);
  
      let current = tree;
      relativeParts.forEach((part, index) => {
        if (!current[part]) {
          current[part] = index === relativeParts.length - 1 ? null : {};
        }
        current = current[part];
      });
    });
  
    return tree;
  };


  // Handle file selection for preview
  const handleSelectFile = async (filePath, skipToggle = false) => {
    // setLoadingPreview(true); 
    setFileKey(filePath);
  
    if (!filePath) { 
      setPreviewContent("No preview available.");
      setFileKey("No file selected.");
      setFileTag("");
      setLoadingPreview(false);
      return;
    }
  
    try {
      const cleanedPath = filePath.split("/").slice(1).join("/"); 
      const presigned = await generatePresignedUrls({
        files: [{ name: cleanedPath }],
        action: "preview",
        datasetName: filePath.split("/")[0], 
      });
      
      setFileTag(presigned[0].tags);

      if (presigned && presigned.length > 0) { 
        setPreviewContent(presigned[0].url); 
        setLoadingPreview(true);

        pendingTagsRef.current[filePath] = presigned[0]?.tags || [];
      } else {
        setPreviewContent("No preview available."); 
      }
    } catch (error) {
      console.error("Error fetching file content:", error);
      setPreviewContent("No preview available.");
    } finally {
      // setLoadingPreview(false); 
    }

    if (!skipToggle) {
      toggleSelection(filePath);
    }

    // toggleSelection(filePath);
  };

  // Toggle file selection for deletion/download
  const toggleSelection = (filePath) => {
    setSelectedFiles((prevSelectedFiles) => {
      let updatedSelection;
  
      if (prevSelectedFiles.includes(filePath)) {
        updatedSelection = prevSelectedFiles.filter(file => file !== filePath);
      } else {
        updatedSelection = [...prevSelectedFiles, filePath];
      }
      // Remove parent folder from selectedFolders if this file was inside it
      const updatedFolders = selectedFolders.filter(folderPath => {
        return !filePath.startsWith(`${folderPath}/`);
      });
  
      setSelectedFolders(updatedFolders);
  
      // Reset preview if nothing selected
      if (updatedSelection.length === 0) {
        setPreviewContent("No preview available."); 
        setFileKey("No file selected.");
      }
  
      return updatedSelection;
    });

  };
  

  const toggleFolderSelection = (folderPath, node) => {
    const getAllFiles = (folder, path = "") => {
      // Return cached result if available
      if (folderFileCache[path]) {
        return folderFileCache[path];
      }
    
      let files = [];
    
      Object.keys(folder).forEach((key) => {
        const fullPath = `${path}/${key}`;
        if (folder[key] === null) {
          files.push(fullPath);
        } else {
          files = [...files, ...getAllFiles(folder[key], fullPath)];
        }
      });
    
      // Cache the result
      folderFileCache[path] = files;
      return files;
    };    
  
    const allFilesInFolder = getAllFiles(node, folderPath);
  
    const alreadySelected = allFilesInFolder.every((file) => selectedFiles.includes(file));
  
    if (alreadySelected) {
      //  Deselect all files in the folder and remove the folder from selection
      setSelectedFiles((prevSelected) =>
        prevSelected.filter((file) => !allFilesInFolder.includes(file))
      );
      setSelectedFolders((prevFolders) =>
        prevFolders.filter((folder) => folder !== folderPath)
      );
    } else {
      // Select all files and mark the folder as selected
      setSelectedFiles((prevSelected) => [
        ...prevSelected,
        ...allFilesInFolder.filter((f) => !prevSelected.includes(f)),
      ]);
      setSelectedFolders((prevFolders) => [
        ...prevFolders,
        folderPath,
      ]);
    }

  };

  
  // Toggle folder collapse state
  const toggleFolderCollapse = (folderPath) => {
    setCollapsedFolders((prev) => {
      const updated = { ...prev };
      updated[folderPath] = !updated[folderPath];
      
      return updated;
    });
  };
  

  const cleanSelections = (selectedFolders, selectedFiles) => {
    // Step 1: Deduplicate folders by removing nested ones
    const uniqueFolders = [...selectedFolders].sort().reduce((acc, folder) => {
      // console.log("Folder:", folder); 
      const isSubfolder = acc.some(parent => folder.startsWith(parent + "/"));
      if (!isSubfolder) acc.push(folder);
      return acc;
    }, []);
  
    // Step 2: Remove files that are under any selected folder
    const filteredFiles = selectedFiles.filter(filePath => {
      // console.log("File Path:", filePath);
      return typeof filePath === 'string' && !uniqueFolders.some(folder => filePath.startsWith(folder + "/"));;
    });
  
    return {
      cleanedFolders: uniqueFolders,
      cleanedFiles: filteredFiles
    };
  };  
  

  const countFilesPerFolder = (successfulUploads = [], failedUploads = []) => {
    const folderCounts = {};
  
    const isImageFile = (filename) => {
      const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
      const ext = filename.slice(filename.lastIndexOf('.')).toLowerCase();
      return imageExtensions.includes(ext);
    };
  
    const countFiles = (uploads, type) => {
      for (const filePath of uploads) {
        const parts = filePath.split('/');
        const folderPath = parts.length > 1 ? parts.slice(0, -1).join('/') : 'root';
        const fileName = parts[parts.length - 1];
  
        if (!folderCounts[folderPath]) {
          folderCounts[folderPath] = {
            folder: folderPath,
            successCount: 0,
            failedCount: 0,
            image_files_count: 0
          };
        }
  
        // Increment success or failed count
        folderCounts[folderPath][type]++;
  
        // Only increment image_files_count if it's not XML
        if (isImageFile(fileName)) {
          folderCounts[folderPath].image_files_count++;
        }
      }
    };
  
    countFiles(successfulUploads, "successCount");
    countFiles(failedUploads, "failedCount");
    // console.log("folderCounts: ", folderCounts);
  
    return Object.values(folderCounts);
  };
  

  const handleUploadMultiple = async (isAddMode, destinationFolder = "") => {
    if (!selectedFiles || selectedFiles.length === 0) {
      alert("Please select a folder to upload.");
      return;
    }
  
    if (!isAddMode) {
      const folderExists = datasetList.some(
        dataset => dataset.folderName === selectedFiles[0].webkitRelativePath.split('/')[0]
      );
    
      if (folderExists) {
        setShowWarningModal(true);
        return;
      }
    }    
  
    // Abort controller
    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;
  
    setIsUploading(true);
    setUploadProgress(0);

    const successfulUploads = [];
    const failedUploads = [];
    const trulyNewUploads = [];
  
    const originalTopFolder = selectedFiles[0].webkitRelativePath.split("/")[0];
    const datasetFolderName = isAddMode ? destinationFolder : originalTopFolder;
    const batches = buildDynamicFileBatches(selectedFiles, 300000);
    const limit = pLimit(10); // limit concurrent uploads per batch
  
    const uploadAction = isAddMode ? "add" : "upload";
    const totalFileCount = selectedFiles.length;
    let completedCount = 0;
  
    console.log("Total batches:", batches.length);

    console.time('Upload Time');
  
    try {
      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        const batch = batches[batchIndex]; 
        // If the upload is canceled, stop further execution
        if (signal.aborted) {
          console.log("Upload was canceled before starting this batch.");
          throw new Error("Upload canceled by user.");
        }
  
        const urls = await generatePresignedUrls({
          files: batch,
          action: uploadAction,
          datasetName: datasetFolderName,
          useFullRelativePath: true,
          isAddMode: isAddMode
        });
  
        console.log(`Signed URLs received for batch ${batches.indexOf(batch) + 1}: ${batch.length}`);
  
        const uploadTasks = urls.map(urlObj =>
          limit(async () => {
            const file = batch.find(f => {
              const relative = f.webkitRelativePath || f.name;
              const cleaned = relative.split("/").slice(1).join("/");
              return cleaned === urlObj.filename;
            });
  
            try {
              if (!file) throw new Error("File not found for URL mapping");

              const cleanedRelativePath = file.webkitRelativePath.split("/").slice(1).join("/");
              const fullS3Path = `${organization}/data/${datasetFolderName}/${cleanedRelativePath}`;
              const fullS3PathWithoutExt = fullS3Path.replace(/\.[^/.]+$/, "");

              if (!existingS3FilesSet.has(fullS3PathWithoutExt) && !trulyNewUploads.includes(fullS3Path)) {
                trulyNewUploads.push(fullS3Path.split("/").slice(3).join("/"));
              }
  
              // Check if the upload was canceled before sending the request
              if (signal.aborted) {
                console.log("Upload was canceled before sending request for", urlObj.filename);
                throw new Error("Upload canceled by user.");
              }
  
              await apiClient.put(urlObj.url, file, {
                headers: { 'Content-Type': urlObj.contentType },
                signal 
              });
  
              successfulUploads.push(urlObj.filename);

            } catch (err) {
              if (apiClient.isCancel(err)) {
                console.warn("Upload canceled:", urlObj.filename);
              } else {
                console.error("Upload failed for:", urlObj.filename, err);
                failedUploads.push(urlObj.filename);
              }

            } finally {
              completedCount++;
              setUploadedCount(completedCount);
              const progress = Math.floor((completedCount / totalFileCount) * 100);
              setUploadProgress(progress);
            }
          })
        );

        console.log("Upload completed. Syncing metadata....")

        await Promise.all(uploadTasks);

        // const folderLevelUploadCounts = countFilesPerFolder(successfulUploads, failedUploads);
        const folderLevelUploadCounts = countFilesPerFolder(trulyNewUploads, failedUploads);

        // Write metadata to DynamoDB via a backend call
        const metadataUpdateResponse = await apiClient.post('datamanagement', {
          action: "writeUploadMetadata",
          organization,
          datasetName: datasetFolderName,
          username,
          folderLevelUploadCounts
        }, 
        // requestConfig
        ); 

        if(metadataUpdateResponse.status === 200){
          alert(`Upload Complete!\n` +
          `New Files: ${trulyNewUploads.length}\n` +
          `Replaced Files: ${successfulUploads.length - trulyNewUploads.length}\n` +
          `Failed: ${failedUploads.length}`);
         }
      }
  
      setUploadProgress(100);
      setTimeout(() => {
        setUploadProgress(0);
        setIsUploading(false);
      }, 1000);
  
      setIsRefreshing(true);
      await fetchDatasets(); 
      handleDatasetChange({ target: { value: datasetFolderName } }); 
      setIsRefreshing(false);
      
    } catch (error) {
      if (error.message === "Upload canceled by user.") {
        alert("Upload was canceled.");
      } else {
        console.error("Error during upload:", error);
      }
    } finally {
      console.timeEnd('Upload Time');
      setIsUploading(false);
      setUploadProgress(0);
      setUploadedCount("");
      handleClearSelection();
    }
  }; 
  
  // Reusable function to generate presigned URLs
  const generatePresignedUrls = async ({ files, action, datasetName, useFullRelativePath = false, isAddMode = false }) => {
    const payload = {
      action: "generatePresignedUrls",
      organization,
      datasetName,
      username,
      operation: action, // "upload", "download", "preview"
      files: files.map(f => {
        const relativePath = typeof f === 'string' ? f : (f.webkitRelativePath || f.name);
        const cleanedPath = relativePath.split("/").slice(1).join("/");
        return {
          name: useFullRelativePath
            ? `${cleanedPath}`
            : `${relativePath}`,
          type: f.type || 'application/octet-stream'
          
        };
        
      })
    };
  
    const { data } = await apiClient.post('datamanagement', payload );
    return data;
  };  
  

  const handleDeleteSelectedFiles = async () => {
    if (!selectedFiles || selectedFiles.length === 0) {
      alert("Please select at least one file to delete.");
      return;
    }
  
    if (!organization) {
      console.error("Error: Organization is undefined");
      return;
    }
  
    if (
      !window.confirm(
        "Once triggered, this action cannot be stopped or undone. Are you sure you want to delete the selected files?"
      )
    ) {
      return;
    }
  
    const { cleanedFolders, cleanedFiles } = cleanSelections(selectedFolders, selectedFiles);
    const totalCount = cleanedFiles.length + cleanedFolders.length;
  
    if (totalCount === 0) {
      alert("Nothing to delete after cleaning selection.");
      return;
    }

    setIsDeleting(true);
    setDeletionResult(null);
    setProgressLabel(`Deleting ${totalCount} files...`);
  
    try {
      const response = await apiClient.post(
        'datamanagement',
        {
          action: "deleteFiles",
          files: cleanedFiles,
          folders: cleanedFolders,
          organization: organization,
        }
      );

      flushSync(() => {
        setSelectedFiles([]);
        setSelectedFolders([]);
        setIsRefreshing(true);
      });
      
      setIsDeleting(false);
      setIsRefreshing(false);
      setDeletionResult({
        status: "success",
        message: response.data.message,
      });
      setProgressLabel(response.data.message);
      await fetchDatasets();
      setIsRefreshing(true);
      const exists = datasetList.some(d => d.folderName === selectedDataset);
      if (exists) {
        fetchFiles(selectedDataset);
      } else {
        setSelectedDataset("");
        setPreviewContent("No preview available."); 
        setFileKey("No file selected.");
      }
      
      setIsRefreshing(false);
      
    } catch (error) {
      console.error("Error deleting files:", error);
      setDeletionResult({
        status: "error",
        message: error?.response?.data?.message || "An error occurred while deleting files.",
      });
    } finally {
      setIsDeleting(false);
      setProgressLabel("");
      setDeletionResult(null);
      setIsRefreshing(false);
    }
  };
  

  // Handle Download
  const handleDownloadFiles = async (selectedFiles) => {
    if (!selectedFiles || !selectedFolders ) return;

    const { cleanedFolders, cleanedFiles } = cleanSelections(selectedFolders, selectedFiles);

    if (!cleanedFolders.length && !cleanedFiles.length) {
      alert("No files or folders selected.");
      return;
    };

    setIsDownloading(true);
    setProgressLabel("Starting download......");
  
    try {
      const response = await apiClient.post('datamanagement', {
            action: "downloadData",
            organization,        
            username,
            folders: cleanedFolders,
            files: cleanedFiles
        }, 
      );

      // console.log(response);
      const { downloadUrl, fileName, message } = response.data;
    
      if (downloadUrl.length > 0){
        setPopupData({
          downloadUrl,
          fileName
        });
        setProgressLabel("");
      }
      else{
        setProgressLabel(message);
      } 
     
    } catch (err) {
      console.error("Error downloading files:", err?.response?.data || err.message);
      alert("Download failed.");
    } finally {
      setIsDownloading(false);
    }
  };
  

  const handleAddData = async(folderPath) => {
    setSelectedPath(folderPath);
    setModalOpen(true);
  };


  const toggleModal = () => setModalOpen(!modalOpen);


  const handleFolderChange = (event, source) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) {
      return;
    }
    setSelectedFiles(files);
    
    const folderPath = files[0].webkitRelativePath;
    if (!folderPath) {
      return;
    }
  
    const selectedFolder = folderPath.split('/')[0];  

    if (!selectedFolder) return;

    if (source === "mainUpload") {
      // Check if folder already exists in any node of the folderTree (including subfolders)
      if (isFolderExistsInTree(datasetList, selectedFolder)) {
        setFolderNameToUpload(selectedFolder);
        setShowWarningModal(true);  // Show warning modal if folder exists
      }
    } 
  };


  // Function to check if the folder already exists in the folder tree
  const isFolderExistsInTree = (folderList, folderName) => {
    return folderList.some(folder => folder.folderName === folderName);
  };  


  const handleCancelUpload = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort(); // Abort all ongoing requests
      abortControllerRef.current = null; // Reset the reference
    }
    setIsUploading(false);
    setUploadProgress(0);

    setShowWarningModal(false);  
    handleClearSelection();
  };


  const handleClearSelection = () => {
    setInputKey(prevKey => prevKey + 1); 
    if (folderInputRef.current) {
      folderInputRef.current.value = "";
    }
    setSelectedFiles([]);
  };

  
  const handleLabelFiles = (selectedFiles) => {
    console.log("selectedFiles: ", selectedFiles);
    if (!selectedFiles || selectedFiles.length === 0){
      alert("Please add at least one label before proceeding!");
      return;
    }
    else{
      setLabelPopupOpen(true);
    }   
  };
  
  // Handle Save of labels
  const handleSaveLabels = async () => {
    setLabelingQueue([...selectedFiles]);
    const trimmedLabel = newLabels.trim();

    if (labelModeName === "single") {
      if (trimmedLabel.length === 0) {
        alert("Please add at least one label before proceeding!");
        return;
      }
      setSavedLabels(trimmedLabel);
    }

    // Preload rectangles for all selected files before starting labeling
    await Promise.all(selectedFiles.map(preloadTagsOnly));
    // if (selectedFiles.length > 0) {
    //   await handleSelectFile(selectedFiles[0], true);
    // }

    setLabelPopupOpen(false);
    setCurrentImageIndex(0);
    setIsLabelingMode(true);
    setRectangles({}); 
    setNewLabels("");
    setCurrentRect(null);

    // if (selectedFiles.length > 0) {
    //   handleSelectFile(selectedFiles[0], true);
    // }
  };

  // Handle Cancel of labels
  const handleCancelLabels = () => {
    setLabelPopupOpen(false);
    setIsLabelingMode(false);
    setNewLabels("");
    annotationDataRef.current = [];
  };
  

  const getRelativeCoords = (e) => {
    const container = previewContainerRef.current;
    if (!container || !isImageReady) return null;

    const rect = container.getBoundingClientRect();
    const offsetX = e.clientX - rect.left - fitBox.left;
    const offsetY = e.clientY - rect.top  - fitBox.top;

    const xClamped = Math.max(0, Math.min(offsetX, fitBox.w));
    const yClamped = Math.max(0, Math.min(offsetY, fitBox.h));

    return {
      xNorm: fitBox.w ? xClamped / fitBox.w : 0,
      yNorm: fitBox.h ? yClamped / fitBox.h : 0,
    };
  };


  // Mouse Events
  const handleMouseDown = (e) => {
    if (!isLabelingMode) return;
    const coords = getRelativeCoords(e);
    if (!coords) return;
  
    setMouseStart(coords);
    setIsDrawing(true);
  };
  
  const handleMouseMove = (e) => {
    if (!isLabelingMode || !isDrawing || !mouseStart) return;
    const coords = getRelativeCoords(e);
    if (!coords) return;
  
    const xMin = Math.min(mouseStart.xNorm, coords.xNorm);
    const yMin = Math.min(mouseStart.yNorm, coords.yNorm);
    const xMax = Math.max(mouseStart.xNorm, coords.xNorm);
    const yMax = Math.max(mouseStart.yNorm, coords.yNorm);
  
    setCurrentRect({ xMin, yMin, xMax, yMax });
  };

  
  const handleMouseUp = async (e) => {
    if (!isLabelingMode || !currentRect) return;

    const currentFile = labelingQueue[currentImageIndex];
    let labeledRect;

    if (labelModeName === "multiple") {
      labeledRect = { ...currentRect, label: "" };
    } else {
      const trimmedLabel = savedLabels.trim();
      labeledRect = { ...currentRect, label: trimmedLabel };
    }

    setAllRectangles(prev => {
      const updated = { ...prev, [currentFile]: [...(prev[currentFile] || []), labeledRect] };
      allRectanglesRef.current = updated;
      return updated;
    });

    if (labelModeName === "multiple") {
      setShowMultiLabelPopUp(true);
      setIsDrawing(false);
      return;
    }

    await new Promise(resolve => setTimeout(resolve, 0));
    console.log(allRectangles);
    setCurrentRect(null);
    setIsDrawing(false);
    await goToNextImage();
  };


  const updateAnnotationLabel = (filename, targetRect, newLabel) => {
    const normalizedLabel = newLabel.trim();
    if (normalizedLabel === "") return;

    setAllRectangles(prev => {
      const updatedRects = (prev[filename] || []).map(rect => {
        if (rect.xMin === targetRect.xMin && rect.yMin === targetRect.yMin &&
            rect.xMax === targetRect.xMax && rect.yMax === targetRect.yMax) {
          return { ...rect, label: normalizedLabel };
        }
        return rect;
      });
      const merged = { ...prev, [filename]: updatedRects };
      allRectanglesRef.current = merged;
      return merged;
    });
  };
 

  const goToNextImage = async () => {
    const nextIndex = currentImageIndex + 1;
    console.log(nextIndex, labelingQueue.length);
  
    if (nextIndex >= labelingQueue.length) {
      console.log("Triggering lambda for labels.....");
      await sendAnnotationsToLambda(allRectanglesRef.current);
      allRectanglesRef.current = {};
      setCurrentImageIndex(0);
      setIsLabelingMode(false);
      alert("Finished labeling all images!");
    } else {
      setCurrentImageIndex(nextIndex);
      // handleSelectFile(labelingQueue[nextIndex], true);
    }
    setNewLabels("");
  };


  const sendAnnotationsToLambda = async (rectanglesState) => {
    try {
      const annotationData = Object.entries(rectanglesState).flatMap(([filename, rects]) => {
        const nat = naturalSizeByFile[filename];
        if (!nat) {
          console.warn("Missing natural size for", filename, "— skipping");
          return [];
        }
        return rects.map((r) => {
          const px = normToPx(r, nat); // convert back to pixels using the file’s natural size
          return {
            filename,
            label: r.label || savedLabels,
            xmin: Number(px.xMin.toFixed(4)),
            ymin: Number(px.yMin.toFixed(4)),
            xmax: Number(px.xMax.toFixed(4)),
            ymax: Number(px.yMax.toFixed(4)),
          };
        });
      });

      await apiClient.post('datamanagement', {
        action: "labelData",
        organization,
        username,
        annotationData,
      });
    } catch (e) {
      console.error("Error uploading annotations:", e);
      alert("Error uploading annotations. Please try again.");
    }
  };


  const deleteRectangle = (filename, rectIndex) => {
    setAllRectangles(prev => {
      const updatedRects = (prev[filename] || []).filter((_, index) => index !== rectIndex);
      return { ...prev, [filename]: updatedRects };
    });
  };


  // Exit Labelling
  const handleExitLabeling = () => {
    setIsLabelingMode(false);
    setCurrentImageIndex(0);
    setRectangles({});
    setSavedLabels("");
    setCurrentRect(null);
    setIsDrawing(false);
    annotationDataRef.current = []; 
  };


  const getAdjacentFile = (fileNamesArray, currentFile, direction) => {
    if (!Array.isArray(fileNamesArray) || !currentFile) return null;
  
    const currentIndex = fileNamesArray.indexOf(currentFile);
    if (currentIndex === -1) return null;
  
    if (direction === "next" && currentIndex < fileNamesArray.length - 1) {
      return fileNamesArray[currentIndex + 1];
    }
  
    if (direction === "previous" && currentIndex > 0) {
      return fileNamesArray[currentIndex - 1];
    }
  
    return null; // No move possible
  };


  const preloadTagsOnly = async (filePath) => {
    const cleanedPath = filePath.split("/").slice(1).join("/");
    const res = await generatePresignedUrls({
      files: [{ name: cleanedPath }],
      action: "preview",
      datasetName: filePath.split("/")[0],
    });
    pendingTagsRef.current[filePath] = res?.[0]?.tags || [];
  };

 
  return (

    <div className="page-wrapper overflow-hidden">
      {/* <div className="headerContainer">
          <NavbarComponent className="col-lg-12"></NavbarComponent>
      </div> */}
    
      <div align="center" style={{ paddingTop: "0px" }}>
            <Row className="justify-content-center" style={{ margin: "0 auto", maxWidth: "1200px" }}>
              <Col sm="12" md="12" style= {{  }}>
                <ComponentCard>
                  <div style={{  maxWidth: "800px", margin: "0 auto" , marginTop: "10px", marginBottom: "0px"}}>
                    {/* Title */}
                    <h4 style={{ fontWeight: "bold", marginBottom: "15px" }}>
                      Upload Data for Model Building
                    </h4>

                    {/* Instructions */}
                    <div style={{ fontSize: "15px", marginBottom: "15px" , textAlign: "left"}}>
                      <p>Please follow the steps below to upload a <code>NEW</code> dataset:</p>
                      <ul style={{ paddingLeft: "18px" }}>
                        <li>
                          Select a dataset folder in an <code>unzipped</code> format.
                        </li>
                        <li>
                          The dataset folder must contain <code>separated individual class folders</code> — one for each class.
                        </li>
                        <li>
                          For example, to upload a <code>"GolfBalls"</code> dataset, your folder structure should look like: <code>GolfBalls / Class X, Class Y, Class Z</code>
                        </li>
                      </ul>
                    </div>

                {/* File Upload Form */}
                <Form style={{ marginBottom: "0" }}>
                  <InputGroup style={{ marginBottom: "0" }}>
                    <Input
                      key={inputKey}
                      type="file"
                      directory=""
                      webkitdirectory=""
                      ref={folderInputRef}
                      onChange={(e) => handleFolderChange(e, "mainUpload")}
                      style={{ marginRight: "10px", maxWidth: "500px" }}
                    />
                    <Button
                      color="outline-secondary"
                      onClick={() => handleUploadMultiple(false)}
                      disabled={uploading}
                      style={{ marginRight: "10px",  backgroundColor: '#eaf9ea', color: 'grey', marginRight: '20px', fontSize: '15px' }}
                    >
                      {uploading ? "Uploading..." : "Upload"}
                    </Button>
                    <Button
                      color="outline-secondary"
                      onClick={() => handleCancelUpload(false)}
                      disabled={!uploading}
                      style={{ marginRight: "10px", marginRight: '20px', fontSize: '15px' }}
                    >
                      Cancel Upload
                    </Button>
                  </InputGroup>
                </Form>
                  {uploading && (
                    <Box sx={{ marginTop: "15px", marginBottom:"0px" }}>
                      <Typography variant="body2">
                        Uploading {uploadedCount} / {selectedFiles.length} files...
                      </Typography>
                      <LinearProgress variant="determinate" value={uploadProgress} sx={{ mt: 1 }} />
                    </Box>
                  )}
                  </div>
                </ComponentCard>

            <DownloadDialog
              open={Boolean(popupData)}
              onClose={() => setPopupData(null)}
              downloadUrl={popupData?.downloadUrl}
              fileName={popupData?.fileName}
            />

            {/* Modal for Folder Overwrite Warning */}
            <Modal isOpen={showWarningModal} toggle={handleCancelUpload} className="custom-modal">
              <ModalHeader toggle={handleCancelUpload} className="modal-header-title" style={{ color: "red", fontWeight: "bold" }}>
                <ErrorOutline style={{ marginRight: "8px", fontSize: "24px" }} />
                Dataset Already Exists
              </ModalHeader>
              <ModalBody>
                <p>You are trying to upload data with name <code>{folderNameToUpload}</code>, which is already in use. If you are indeed uploading new data, please rename your folder and upload to avoid overwriting the existing dataset with this name.</p>
                <p>If you are trying to add more data into an existing dataset, Click on the + button next to the dataset you want to add to instead.</p>
              </ModalBody>
              <ModalFooter>
                {/* <Button color="primary" onClick={() => handleUploadMultiple(true)}>Continue</Button> */}
                <Button color="secondary" onClick={handleCancelUpload}>Cancel</Button>
              </ModalFooter>
            </Modal>
          </Col>

          <Col style={{ marginTop: 10 }}>
            <ComponentCard>
              <div style={{  maxWidth: "800px", margin: "0 auto" , marginTop: "10px", marginBottom: "0px"}}>
              <h4 style={{ fontWeight: "bold", marginBottom: "20px", marginTop:"0px", maxWidth: "800px" }}>
                File Explorer
              </h4>
                <label style = {{ fontSize: "15px", marginBottom: "10px" }}>
                  Select an existing Dataset to view in File Explorer.
                </label>
      
                <Form>
                  <Row className="align-items-center">
                    <Col md="2">
                      {/* <label style={{ fontWeight: "bold" }}>Select Dataset</label> */}
                    </Col>
                    <Col md="8">
                      <Input type="select" 
                            value={selectedDataset} 
                            onChange={handleDatasetChange}
                            >
                        <option value="">-- Select --</option>
                        {datasetList.map((dataset, idx) => (
                            <option key={idx} value={dataset.folderName}>
                              {dataset.folderName} - {dataset.updatedttm}
                            </option>
                          ))}
                      </Input>
                    </Col>
                    <Col md="2"></Col>
                  </Row>
                </Form>
              </div>
            <br></br>
            <div style={{  maxWidth: "1000px", margin: "0 auto" , marginTop: "10px", marginBottom: "0px"}}>
              <Row  className="d-flex justify-content-center" style={{ paddingTop: "0px", marginBottom: "20px" }}>
                <Col></Col>
                <Col sm="auto" md="auto" className="text-center">
                  <Button 
                    className="danger-action-button"
                    onClick={handleDeleteSelectedFiles} 
                    style={{ marginLeft: "10px" }} 
                    disabled={selectedFiles.length === 0 || isDeleting || isDownloading}>
                    Delete Selected Files
                  </Button>
                </Col>
                {/* <Col sm="auto" className="text-center">
                  <Button 
                    className="submit-action-button"
                    onClick={() => handleDownloadFiles(selectedFiles)} 
                    disabled={selectedFiles.length === 0 || isDownloading || isDeleting}>
                    {isDownloading ? "Downloading..." : "Download Selected Files"}
                  </Button>
                </Col> */}
                <Col sm="auto" className="text-center">
                  <Button 
                    className="submit-action-button"
                    onClick={isLabelingMode ? handleExitLabeling : () => handleLabelFiles(selectedFiles)}
                    disabled={(selectedFiles.length === 0 && !isLabelingMode) || isDownloading || isDeleting}
                  >
                    {isLabelingMode ? "Exit Labelling Images" : "Label Selected Files"}
                  </Button>
                </Col>
                <Col></Col>
              </Row>
              <Row className="d-flex justify-content-center" style={{ paddingTop: "0px", marginBottom: "20px" }}>
              {(isDeleting || isDownloading ) && progressLabel && (
                    <div style={{ marginTop: "8px", color: "#28a745" }}>
                      <span className="spinner" style={{ color: "#28a745" }}/>
                      <span>{progressLabel}</span>
                    </div>
                  )}
                  {deletionResult && (
                      <div
                        style={{
                          marginTop: "10px",
                          color: deletionResult.status === "success" ? "green" : "red",
                        }}
                      >
                        {deletionResult.message}
                      </div>
                    )}
              </Row>
              
              <Row  style={{ paddingTop: "0px", marginBottom: "20px", alignContent: "left" }}>    
                {isRefreshing && <div><strong>Refreshing file list...</strong></div>}
              </Row>
              <Row  style={{ paddingTop: "0px", marginBottom: "20px", alignContent: "left" }}>    
                {isLabelingMode && labelModeName === "single" && (
                  <div><strong>Adding label: {savedLabels}</strong></div>    
                )}     
              </Row>
              <Row style={{ paddingTop: "0px", marginBottom: "20px" }}>
                  {/* File List Section */}
                  <Col sm="6" md="6">
                    <Card style={{ border: "1px solid #ddd", minHeight: "400px", minWidth: "400px"}}>
                      <CardBody style={{
                          fontSize: "13px",
                          height: "450px",        
                          overflowY: "auto",          
                          border: "1px solid #ddd",   
                          borderRadius: "6px",
                          backgroundColor: "#f9f9f9" 
                          }}>
                    
                          {Object.entries(folderTree).map(([key, node]) => (
                            <div key={key} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            {/* Add Button */}
                            <div className="add-data-container">
                            <Button className="add-data-button" color="success" size="sm" style={{ padding: "2px 6px", fontSize: "10px" }} onClick={() => handleAddData(key)}>
                              <FontAwesomeIcon icon={faPlus} style={{ fontSize: "10px" }} />
                            </Button>
                            </div>
                            {/* Folder Name */}
                            <TreeNode
                              key={key}
                              name={key}
                              node={node}
                              path={key}
                              handleSelectFile={handleSelectFile}
                              handleSelectFolder={toggleFolderSelection}
                              handleSelectItem={toggleSelection}
                              handleToggleCollapse={toggleFolderCollapse}
                              collapsedFolders={collapsedFolders}
                              selectedFiles={selectedFiles}
                              selectedFolders={selectedFolders}
                              folderMetadata={folderMetadata}
                              fileKey={fileKey}
                              focusedIndex={focusedIndex}
                              // fileList={fileList}
                            />
                            </div>
                          ))}
                    
                          {nextContinuationToken && !isRefreshing && (
                              <Button
                                  onClick={() => fetchFiles(selectedDataset, nextContinuationToken)}
                                  color="primary"
                                  size="sm"
                                  block
                                  style={{ marginTop: "10px" }}
                                  disabled={!nextContinuationToken || isRefreshing || !selectedDataset} 
                              >
                                  Load More
                              </Button>
                          )}
                      </CardBody>
                    </Card>
                  </Col>

                {/* File Preview Section*/}
                <Col sm="6" md="6">
                  <Card style={{ border: "1px solid #ddd", minHeight: "450px", minWidth: "400px"}}>
                    <CardBody style={{minHeight: "400px"}}>
                      <h6>File Preview</h6>

                      <div
                        ref={previewContainerRef}
                        style={{
                          position: "relative",
                          width: "400px",
                          height: "250px",
                          overflow: "hidden",
                          border: "1px solid #ccc",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "#fff",
                        }}
                      >
                        {/* Image */}
                        {previewContent?.startsWith("http") && (
                          <img
                            key={previewContent}              // ensure onLoad runs on src change
                            ref={imgRef}
                            src={previewContent}
                            alt="Preview"
                            decoding="async"
                            loading="eager"
                            fetchPriority="high"
                            draggable={false}
                            onLoad={(e) => {
                              const { naturalWidth, naturalHeight } = e.currentTarget;

                              setIsImageReady(true);
                              setLoadingPreview(false);
                              computeFitBox();

                              // Cache natural size for this file (used when sending to backend)
                              if (fileKey) {
                                setNaturalSizeByFile((prev) => ({
                                  ...prev,
                                  [fileKey]: { w: naturalWidth, h: naturalHeight },
                                }));
                              }

                              // Normalize any raw pixel tags we fetched earlier
                              const rawTags =
                                pendingTagsRef.current[fileKey] ||
                                (Array.isArray(fileTag) ? fileTag : []) ||
                                [];

                              if (rawTags.length) {
                                setAllRectangles((prev) => ({
                                  ...prev,
                                  [fileKey]: rawTags.map((t) => ({
                                    ...pxToNorm(
                                      { xmin: t.xmin, ymin: t.ymin, xmax: t.xmax, ymax: t.ymax },
                                      { w: naturalWidth, h: naturalHeight }
                                    ),
                                    label: t.name || "",
                                  })),
                                }));
                              }
                            }}
                            onError={() => {
                              setIsImageReady(false);
                              setLoadingPreview(false);
                            }}
                            onMouseDown={handleMouseDown}
                            onMouseMove={handleMouseMove}
                            onMouseUp={handleMouseUp}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "contain",
                              pointerEvents: isImageReady ? "auto" : "none",
                              visibility: isImageReady ? "visible" : "hidden",
                              cursor: isLabelingMode ? "crosshair" : "default",
                            }}
                          />
                        )}

                        {/* Spinner overlay */}
                        {loadingPreview && (
                          <div
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              height: "100%",
                              width: "100%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: "rgba(255, 255, 255, 0.6)",
                              zIndex: 100,
                            }}
                          >
                            <Spinner color="primary" />
                          </div>
                        )}

                        {/* Stored rectangles (normalized -> fitted pixels with letterboxing offsets) */}
                        {isImageReady && (allRectangles[fileKey] || []).map((rect, index) => {
                          const x = fitBox.left + rect.xMin * fitBox.w;
                          const y = fitBox.top  + rect.yMin * fitBox.h;
                          const width  = (rect.xMax - rect.xMin) * fitBox.w;
                          const height = (rect.yMax - rect.yMin) * fitBox.h;

                          return (
                            <div key={`rect-${index}`} style={{ position: "absolute", left: 0, top: 0 }}>
                              <div
                                style={{
                                  position: "absolute",
                                  left: x, top: y, width, height,
                                  border: "2px solid green",
                                  backgroundColor: "rgba(0,255,0,0.1)",
                                  zIndex: 20, pointerEvents: "none",
                                }}
                              />
                              <div
                                style={{
                                  position: "absolute",
                                  left: x,
                                  top: Math.max(y - 16, fitBox.top),
                                  backgroundColor: "rgba(0,0,0,0.6)",
                                  color: "white",
                                  padding: "1px 4px",
                                  fontSize: "10px",
                                  zIndex: 21,
                                }}
                              >
                                {rect.label}
                              </div>

                              {isLabelingMode && (
                                <div
                                  style={{
                                    position: "absolute",
                                    left: x + width - 18,
                                    top: y + 2,
                                    zIndex: 22,
                                    cursor: "pointer",
                                    color: "white",
                                    backgroundColor: "red",
                                    borderRadius: "50%",
                                    width: 16,
                                    height: 16,
                                    textAlign: "center",
                                    fontSize: 12,
                                    lineHeight: "16px",
                                    boxShadow: "0 0 2px rgba(0,0,0,0.5)",
                                  }}
                                  onClick={() => deleteRectangle(fileKey, index)}
                                  title="Delete Rectangle"
                                >
                                  ×
                                </div>
                              )}
                            </div>
                          );
                        })}

                        {/* Live drawing box */}
                        {isDrawing && currentRect && isImageReady && (
                          <div
                            style={{
                              position: "absolute",
                              left: fitBox.left + currentRect.xMin * fitBox.w,
                              top:  fitBox.top  + currentRect.yMin * fitBox.h,
                              width:  (currentRect.xMax - currentRect.xMin) * fitBox.w,
                              height: (currentRect.yMax - currentRect.yMin) * fitBox.h,
                              border: "2px dashed blue",
                              backgroundColor: "rgba(0,0,255,0.1)",
                              zIndex: 30,
                              pointerEvents: "none",
                            }}
                          />
                        )}
                      </div>

                      <div
                        style={{
                          marginTop: "30px",
                          fontSize: "12px",
                          color: "#666",
                          wordBreak: "break-all",
                          minHeight: "48px",
                        }}
                      >
                        <div style={{ color: "black"}}>
                          <span style={{ fontWeight: "bold", marginRight: "5px" }}>File Name:</span>{" "}
                          {loadingPreview ? "Loading..." : fileKey}
                          <br />
                          </div>
                          <div style={{ marginTop: "10px", color: "black" }}>
                          <span style={{ fontWeight: "bold", marginRight: "5px"}}>
                            {isLabelingMode ? "Pending Labels:" : "Available Labels:"}
                          </span>{" "}
                          {loadingPreview ? (
                              "Loading..."
                          ) : isLabelingMode ? (
                            labelModeName === "single" ? (
                              savedLabels || "No label set"
                            ) : (() => {
                              const pending = allRectangles[fileKey] || [];
                              return pending.length > 0
                                ? pending.map((p) => p.label).join(", ")
                                : "No labels added yet";
                            })()
                          ) : Array.isArray(fileTag) && fileTag.length > 0 ? (
                            fileTag.map((tag) => tag.name).join(", ")
                          ) : (
                            "No tags found"
                          )}
                        </div>
                      </div>

                      <div className="pagination-controls" 
                          style={{
                            marginTop: "20px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center"
                          }}
                      >
                        <Button size="sm"
                          disabled={isLabelingMode || fileNamesArrayRef.current.indexOf(fileKey) <= 0}
                          onClick={() => {
                            const prevFile = getAdjacentFile(fileNamesArrayRef.current, fileKey, "previous");
                            if (prevFile) handleSelectFile(prevFile);
                          }}
                          hidden={!previewContent}
                        >Prev</Button>

                        {isLabelingMode && labelModeName === "multiple" && (
                          <>
                          {previewContent && selectedFiles.length > 0 && (
                            <Button size="sm" color="primary" onClick={goToNextImage}>
                              Submit
                            </Button>
                          )}
                          </>
                        )}

                        <Button size="sm"
                          disabled={isLabelingMode || fileNamesArrayRef.current.indexOf(fileKey) >= fileNamesArrayRef.current.length - 1}
                          onClick={() => {
                            const nextFile = getAdjacentFile(fileNamesArrayRef.current, fileKey, "next");
                            if (nextFile) handleSelectFile(nextFile);
                          }}
                          hidden={!previewContent}
                        >Next</Button>
                    </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>

                {/* Modal for Adding Data */}
              <Modal isOpen={modalOpen} toggle={toggleModal} className="custom-modal">
                  <ModalHeader toggle={toggleModal} className="modal-header-title">
                    Add New Item</ModalHeader>
                  <ModalBody>
                    <p>You're adding an item to directory:  <strong>{selectedPath}</strong></p>
                    <p><strong>NOTE:</strong></p>
                    <p>1. Select a dataset folder in an <code>unzipped</code> format for upload.</p>
                    <p>2. Upload <mark><strong>only the new data</strong></mark> to avoid running into upload costs and failures.</p>
                    <p>3. Any files already present in the destination will be overwritten and new ones will be added to the existing data.</p>
                    {/* File Upload Section */}
                    <Form>
                      <InputGroup style={{ maxWidth: "600px" }}>
                          <Input key={inputKey}
                          type="file"
                          directory=""
                          webkitdirectory=""
                          ref={folderInputRef}
                          onChange={(e) => handleFolderChange(e, "addData")}
                        />
                        <Button color="outline-secondary" 
                          onClick={() => handleUploadMultiple(true, selectedPath)} 
                          disabled={uploading}
                          style={{ marginRight:"10px", backgroundColor: '#a8d5ba' , color: 'black' }}>
                          {uploading ? "Uploading..." : "Upload"}
                        </Button>
                        <Button
                          color="outline-secondary"
                          onClick={() => handleCancelUpload(false)}
                          disabled={!uploading}
                        >
                          Cancel Upload
                        </Button>
                      </InputGroup>
                    </Form>
                    {uploading && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2">
                          Uploading {uploadedCount} / {selectedFiles.length} files...
                        </Typography>
                        <LinearProgress variant="determinate" value={uploadProgress} sx={{ mt: 1 }} />
                      </Box>
                    )}

                  </ModalBody>
                  <ModalFooter>
                    <Button color="secondary" onClick={toggleModal}>Cancel</Button>
                  </ModalFooter>
              </Modal>

              <Modal isOpen={labelPopupOpen} toggle={handleCancelLabels}>
                <ModalHeader toggle={handleCancelLabels}>Add Labels</ModalHeader>
                <ModalBody>
                  {/* Radio buttons for selecting label mode */}
                  <div style={{ display: "flex", justifyContent: "center", marginBottom: "15px"  }}>
                      <label style={{ marginRight: "40px" }}>
                        <input
                          type="radio"
                          value="single"
                          checked={labelModeName === "single"}
                          onChange={() => {
                            setLabelModeName("single");
                            setNewLabels("");
                          }}
                        />
                        {' '}Single Label
                      </label>
                      <label>
                        <input
                          type="radio"
                          value="multiple"
                          checked={labelModeName === "multiple"}
                          onChange={() => {
                            setLabelModeName("multiple");
                            setNewLabels("");
                          }}
                        />
                        {' '}Multiple Labels
                      </label>
                    </div>

                    {/* Conditional rendering based on mode */}
                    {labelModeName === "single" ? (
                        <div style={{ display: "flex", alignItems: "center", marginBottom: "10px" }}>
                          <Input
                            placeholder="Titleist, ProV, 2020 etc."
                            value={newLabels}
                            onChange={(e) => setNewLabels(e.target.value)}
                          />
                        </div>
                    ) : (
                      <p style={{ fontStyle: "italic", textAlign: "center" }}>
                        Select areas to label and add label names manually to each image.
                      </p>
                    )}
                  </ModalBody>
                  <ModalFooter style={{ display: "flex", justifyContent: "center" }}>
                    <Button color="secondary" style={{ marginRight: "30px" }} onClick={handleCancelLabels}>Cancel</Button>
                    <Button color="success" onClick={handleSaveLabels}>Save</Button>
                  </ModalFooter>
                </Modal>
                
                {currentRect && labelModeName === "multiple" && !isDrawing && (
                  <Modal isOpen={showMultiLabelPopUp} toggle={() => setShowMultiLabelPopUp(false)}>
                      <ModalHeader toggle={() => setShowMultiLabelPopUp(false)}>
                        Add Label
                      </ModalHeader>
                      <ModalBody>
                        <Input
                            placeholder="Enter label"
                            value={multiLabelInput}
                            onChange={(e) => {
                              setMultiLabelInput(e.target.value);
                            }}
                        />
                      </ModalBody>
                      <ModalFooter style={{ justifyContent: "center" }}>
                        <Button
                            color="success"
                            disabled={multiLabelInput.trim() === ""}
                            onClick={() => {
                                const currentLabel = multiLabelInput.trim();
                                if (currentLabel !== "") {
                                  updateAnnotationLabel(
                                      labelingQueue[currentImageIndex],  
                                      currentRect,
                                      currentLabel
                                    );
                                  }
                                  setMultiLabelInput("");
                                  setShowMultiLabelPopUp(false);
                                  setCurrentRect(null);
                                  setIsDrawing(false);
                              }}
                          >
                            ✔
                        </Button>
                        <Button
                            color="danger"
                            onClick={() => {
                              setMultiLabelInput("");
                              setShowMultiLabelPopUp(false);
                            }}
                        >
                            ✖
                        </Button>
                      </ModalFooter>
                  </Modal>    
                )} 
              </div>
            </ComponentCard>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default DataManagement;